import React, { useEffect, useState, useContext } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useLocation } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { LanguageContext } from '../../Context/LanguageContext';

const servicesData = {
  en: [
    {
      id: 1,
      title: 'Web Development',
      description: 'Build powerful websites and web apps tailored to your business needs.',
      icon: 'fa-solid fa-code',
      search_title: 'web-development',
    },
    {
      id: 2,
      title: 'UI/UX Design',
      description: 'Design modern and intuitive interfaces that boost user engagement.',
      icon: 'fa-solid fa-palette',
      search_title: 'ui-ux-design',
    },
    {
      id: 3,
      title: 'Mobile Applications',
      description: 'Develop custom mobile apps with smooth performance on all platforms.',
      icon: 'fa-solid fa-mobile-screen-button',
      search_title: 'mobile-apps',
    },
    {
      id: 4,
      title: 'SEO Optimization',
      description: 'Improve your website ranking and visibility in search engines.',
      icon: 'fa-solid fa-chart-line',
      search_title: 'seo-optimization',
    },
    {
      id: 5,
      title: 'Branding & Identity',
      description: 'Create a strong visual identity and consistent brand experience.',
      icon: 'fa-solid fa-bullhorn',
      search_title: 'branding-identity',
    },
    {
      id: 6,
      title: 'E-Commerce Solutions',
      description: 'Launch your online store with reliable and scalable solutions.',
      icon: 'fa-solid fa-cart-shopping',
      search_title: 'ecommerce-solutions',
    },
  ],
  ar: [
    {
      id: 1,
      title: 'تطوير المواقع',
      description: 'نبني مواقع وتطبيقات ويب قوية ومخصصة تلائم احتياجات عملك.',
      icon: 'fa-solid fa-code',
      search_title: 'web-development',
    },
    {
      id: 2,
      title: 'تصميم UI/UX',
      description: 'نصمم واجهات حديثة وسهلة الاستخدام تزيد من تفاعل المستخدم.',
      icon: 'fa-solid fa-palette',
      search_title: 'ui-ux-design',
    },
    {
      id: 3,
      title: 'تطبيقات الموبايل',
      description: 'نطور تطبيقات مخصصة بأداء سلس على جميع المنصات.',
      icon: 'fa-solid fa-mobile-screen-button',
      search_title: 'mobile-apps',
    },
    {
      id: 4,
      title: 'تحسين محركات البحث',
      description: 'نساعدك على تحسين ترتيب موقعك في نتائج محركات البحث.',
      icon: 'fa-solid fa-chart-line',
      search_title: 'seo-optimization',
    },
    {
      id: 5,
      title: 'بناء الهوية البصرية',
      description: 'نبتكر هوية بصرية متناسقة وقوية لتمييز علامتك التجارية.',
      icon: 'fa-solid fa-bullhorn',
      search_title: 'branding-identity',
    },
    {
      id: 6,
      title: 'حلول التجارة الإلكترونية',
      description: 'نساعدك على إطلاق متجرك الإلكتروني بشكل احترافي وموثوق.',
      icon: 'fa-solid fa-cart-shopping',
      search_title: 'ecommerce-solutions',
    },
  ],
};

const projectsData = [
  {
    service: 'web-development',
    title: { en: 'E-Commerce Platform', ar: 'منصة تجارة إلكترونية' },
    description: {
      en: 'A full e-commerce site for electronics.',
      ar: 'موقع تجارة إلكترونية متكامل للإلكترونيات.',
    },
    image: '/assets/images/project1.png',
  },
  {
    service: 'web-development',
    title: { en: 'Corporate Website', ar: 'موقع شركة' },
    description: {
      en: 'Modern website for a law firm.',
      ar: 'موقع حديث لمكتب محاماة.',
    },
    image: '/assets/images/project2.png',
  },
  {
    service: 'seo-optimization',
    title: { en: 'SEO for Blog', ar: 'تحسين مدونة' },
    description: {
      en: 'Increased blog traffic by 200%.',
      ar: 'زيادة عدد زوار المدونة بنسبة 200٪.',
    },
    image: '/assets/images/project3.png',
  },
  {
    service: 'seo-optimization',
    title: { en: 'Local SEO', ar: 'SEO محلي' },
    description: {
      en: 'Improved Google My Business visibility.',
      ar: 'تحسين الظهور في Google My Business.',
    },
    image: '/assets/images/project4.png',
  },
  {
    service: 'branding-identity',
    title: { en: 'Brand Refresh', ar: 'تجديد العلامة التجارية' },
    description: {
      en: 'Updated brand identity for a startup.',
      ar: 'تجديد الهوية البصرية لشركة ناشئة.',
    },
    image: '/assets/images/project5.png',
  },
  {
    service: 'ecommerce-solutions',
    title: { en: 'Shopify Store', ar: 'متجر شوبيفاي' },
    description: {
      en: 'Custom Shopify store setup.',
      ar: 'إعداد متجر شوبيفاي مخصص.',
    },
    image: '/assets/images/project6.png',
  },
];

function ServicesDetails() {
  const { language } = useContext(LanguageContext);
  const location = useLocation();
  const servicePath = location.pathname.split('/')[2];

  const [currentPage, setCurrentPage] = useState(0);
  const projectsPerPage = 4;

  useEffect(() => {
    AOS.init({ duration: 1200, once: true });
  }, []);

  const service = servicesData[language].find(s => s.search_title === servicePath);

  const filteredProjects = projectsData.filter(project => project.service === servicePath);

  const offset = currentPage * projectsPerPage;
  const currentProjects = filteredProjects.slice(offset, offset + projectsPerPage);
  const pageCount = Math.ceil(filteredProjects.length / projectsPerPage);
  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  if (!service) {
    return (
      <div className="services_details p-5" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="container text-center">
          <h2 className="text-danger">
            {language === 'ar' ? 'الخدمة غير موجودة' : 'Service Not Found'}
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div
      className="services_details p-5"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      style={{ backgroundColor: '#f7f9fc', minHeight: '100vh' }}
    >
      <div
        className="container mt-5 card border-0 shadow-lg p-0 rounded-4 overflow-hidden"
        style={{ maxWidth: 1200 }}
      >
        {/* Header */}
        <div
          className="w-100 mb-4 d-flex flex-column align-items-center justify-content-center position-relative"
          style={{
            backgroundImage: "url('/assets/images/service-bg.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            height: '280px',
            color: 'white',
            textShadow: '0 2px 10px rgba(0,0,0,0.6)',
          }}
          data-aos="zoom-in"
        >
          <div
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 0 }}
          />
          <i
            className={service.icon}
            style={{ fontSize: '4rem', marginTop: '40px', zIndex: 1, position: 'relative' }}
          />
          <h2 className="fw-bold mt-3 mb-4" style={{ zIndex: 1, position: 'relative' }}>
            {service.title}
          </h2>
        </div>

        {/* Description */}
        <div
          className="card-body px-5"
          data-aos="fade-up"
          data-aos-delay="200"
          style={{ minHeight: 120, backgroundColor: 'white', borderRadius: '0 0 20px 20px' }}
        >
          <p
            className="text-secondary"
            style={{ lineHeight: '1.8', textAlign: language === 'ar' ? 'right' : 'left', fontSize: '1.1rem' }}
          >
            {service.description}
          </p>
        </div>

        {/* Related Projects */}
        <div className="container mt-5">
          <h4
            className="text-primary fw-bold text-center mb-5"
            data-aos="fade-up"
          >
            {language === 'ar' ? 'مشاريع ذات صلة' : 'Related Projects'}
          </h4>

          <div className="row g-4">
            {currentProjects.map((project, index) => (
              <div
                key={index}
                className="col-md-6 col-lg-3"
                data-aos="fade-up"
                data-aos-delay={index * 150}
              >
                <div
                  className="card h-100 shadow-sm border-0 rounded-4 project-card"
                  style={{ cursor: 'pointer', overflow: 'hidden', transition: 'transform 0.3s ease, box-shadow 0.3s ease' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 25px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.1)';
                  }}
                >
                  <img
                    src={project.image}
                    alt={project.title[language]}
                    className="card-img-top rounded-top-4"
                    style={{ height: '160px', objectFit: 'cover' }}
                  />
                  <div className="card-body d-flex flex-column">
                    <h6 className="fw-bold">{project.title[language]}</h6>
                    <p className="text-muted" style={{ fontSize: '0.9rem', flexGrow: 1 }}>
                      {project.description[language]}
                    </p>
                    <button
                      className="btn btn-primary mt-auto align-self-start"
                      style={{ fontSize: '0.85rem', padding: '0.3rem 0.8rem' }}
                    >
                      {language === 'ar' ? 'عرض المشروع' : 'View Project'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredProjects.length > projectsPerPage && (
            <div className="d-flex justify-content-center mt-5">
              <ReactPaginate
                previousLabel={language === 'ar' ? 'السابق' : 'Previous'}
                nextLabel={language === 'ar' ? 'التالي' : 'Next'}
                pageCount={pageCount}
                onPageChange={handlePageClick}
                containerClassName="pagination gap-2"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                previousClassName="page-item"
                previousLinkClassName="page-link"
                nextClassName="page-item"
                nextLinkClassName="page-link"
                breakLabel="..."
                breakClassName="page-item"
                breakLinkClassName="page-link"
                activeClassName="active"
                forcePage={currentPage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ServicesDetails;
